import React, { useState, Fragment, useEffect, useRef, useCallback } from "react";
import searchIcon from "../../images/feed_images/search_icon.png";
import chevronDownIcon from "../../images/feed_images/chevron_down.png";
import { MediaFilterParams } from "api/mediaApi";

interface SearchFeedProps {
    filters: Partial<MediaFilterParams>;
    setFilters: React.Dispatch<React.SetStateAction<Partial<MediaFilterParams>>>;
    categories: Record<string, string[]>;
    riskTypes: string[];
    locationTree: Record<string, any>;
}

const riskColorMap: Record<string, string> = {
    "market acceptance": "#9b59b6",
    "sustainability": "#27ae60",
    "cyber": "#2980b9",
};

const ANY = "Any";

/* Filter Dropdowns */ 
const Dropdown = ({
    label,
    options,
    selected,
    onSelect,
    index,
    openIndex,
    setOpenIndex,
    keyName,
}: {
    label: string;
    options: string[];
    selected: string | undefined;
    onSelect: (key: string, value: string) => void;
    index: number;
    openIndex: number | null;
    setOpenIndex: React.Dispatch<React.SetStateAction<number | null>>;
    keyName: string;
}) => {
    const isOpen = openIndex === index;

    return (
        <div className="w-1/3 min-w-0 overflow-hidden">
            <button
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="flex items-center text-left w-full text-sm xl:text-xl px-[12px] xl:px-6 xl:h-[57px] h-[45px] text-[#373940] bg-white border border-[#D9DADF] rounded-full focus:outline-none overflow-hidden"
            >
                <span className="truncate w-full">
                    {selected === ANY ? ANY : selected || label}
                </span>
                <img src={chevronDownIcon} alt="Chevron" className="ml-[12px] w-[13px] flex-shrink-0" />
            </button>

            {isOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 z-30 p-[22px] w-[287px] max-w-[90vw] rounded-md shadow-lg bg-white z-10 lg:left-auto lg:translate-x-0">
                    <ul className="py-1">
                        {options.map((option, optIdx) => {
                            const isSelected = selected === option;
                            const style: React.CSSProperties = {};

                            if (keyName === "riskDimension" && option !== ANY) {
                                const color = riskColorMap[option.toLowerCase()] || "#d35400";
                                style.color = color;
                                style.fontWeight = "bold";
                            }

                            return (
                                <li
                                    key={optIdx}
                                    onClick={() => {
                                        onSelect(keyName, option);
                                        setOpenIndex(null);
                                    }}
                                    className={`cursor-pointer px-4 py-3 text-base hover:bg-gray-100 ${isSelected ? "bg-gray-200 font-semibold" : ""}`}
                                    style={style}
                                >
                                    {option}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

function SearchFeed({ filters, setFilters, categories, riskTypes, locationTree }: SearchFeedProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSelect = useCallback((key: string, value: string) => {
        setFilters(prev => {
            const updated = { ...prev, [key]: value };
            if (key === "category") updated.subCategory = ANY;
            return updated;
        });
    }, [setFilters]);

    // auto-update subCategory if not valid under selected category
    useEffect(() => {
        const currentCat = filters.category;
        const subCat = filters.subCategory;

        if (
            currentCat &&
            currentCat !== ANY &&
            subCat !== ANY &&
            subCat &&
            categories[currentCat]?.length &&
            !categories[currentCat].includes(subCat)
        ) {
            const firstSub = categories[currentCat][0] || ANY;
            setFilters(prev => ({
                ...prev,
                subCategory: firstSub,
            }));
        }
    }, [filters.category, filters.subCategory, categories]);


    // close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenIndex(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const DROPDOWNS = [
        {
            label: "Location",
            options: [ANY, ...Object.keys(locationTree || {})],
            key: "country",
        },
        {
            label: "Risk",
            options: [ANY, ...riskTypes],
            key: "riskDimension",
        },
        {
            label: "Category",
            options: [ANY, ...Object.keys(categories || {})],
            key: "category",
        },
        {
            label: "SubCategory",
            options: [ANY, ...(categories?.[filters.category || ""] || [])],
            key: "subCategory",
        },
    ];

    return (
        <Fragment>
            <div className="container lg:mt-7 mb-8">
                <div className="lg:flex items-start justify-between">
                    {/* Search Bar */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-2 pl-4 flex items-center pointer-events-none">
                            <img src={searchIcon} alt="Search" className="w-[17px] h-[22px]" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search"
                            className="lg:w-[383px] w-full xl:h-[57px] h-[45px] placeholder:text-xl placeholder:font-light placeholder:text-[#767676] text-base pl-[51px] pr-4 py-4 bg-[#F6F6F6] rounded-full border-0 focus:outline-none focus:ring-0 focus:border-inputBorderColor"
                        />
                    </div>

                    {/* Dropdowns */}
                    <div
                        ref={dropdownRef}
                        className="lg:w-1/2 relative w-full flex flex-nowrap gap-[12px] mt-[12px] lg:mt-0 justify-start md:gap-4 lg:justify-end"
                    >
                        {DROPDOWNS.map((dropdown, idx) => (
                            <Dropdown
                                key={dropdown.key}
                                label={dropdown.label}
                                options={dropdown.options}
                                selected={filters[dropdown.key as keyof typeof filters] as string | undefined}
                                onSelect={handleSelect}
                                index={idx}
                                openIndex={openIndex}
                                setOpenIndex={setOpenIndex}
                                keyName={dropdown.key}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </Fragment>
    );
}
export default SearchFeed;